import React from "react"
import autobind from "class-autobind"
import { Paper, TextField, Toggle, SelectField, MenuItem } from "material-ui"
import Autocomplete, { geocodeByAddress } from "react-places-autocomplete"
import LocationMap from "./LocationMap"
import AccountStore from "../../stores/AccountStore"
import strings from "../../localization/strings"
import _ from "lodash"
import "./Location.css"
import * as api from "../../api"
import { IAddress, IGeoLocation, IMicroLocationDocument } from "../../models"
import AppStateStore from "../../stores/AppStateStore"

interface IProps {
    jobLocation?: boolean
    parentHierarchyId?: string
    readonly: boolean
    objectId: string
    disabledGPSLocation?: boolean
    disabledMicroLocation?: boolean
    geoLocation: IGeoLocation
    handleChangeGeoLocation(geoLocation: IGeoLocation): void
}

interface IState {
    address: IAddress
    timezoneId: string
    allowGPSLocation: boolean
    allowMicroLocation: boolean
    disableLocationExceptions: boolean
    addressSearch: string
    latitude: string
    errorLatitude: string
    longitude: string
    errorLongitude: string
    geoFencePerimeter: number
    errorGeoFencePerimeter: string
    microLocationValidation: boolean
    microLocation: IMicroLocationDocument
    beaconCodeErrorText: string
}
const reg = /^-?\d*(\.\d+)?$/

export default class Location extends React.Component<IProps, IState>  {
    private defaultTimezoneId = AccountStore.getCurrentAccount().AccountSettings.LocalizationSettings.DefaultIANATimezoneId
    private Geocoder
    private defaultGeoFencePerimeter = AccountStore.getCurrentAccount().AccountSettings.LocationSettings.DefaultGeoFencePerimeter
    constructor(props: IProps) {
        super(props)
        const address = props.geoLocation.Location && props.geoLocation.Location.Address
        if (props.jobLocation && props.parentHierarchyId) {
            const hierarchyItem = AccountStore.getHierarchyItem(props.parentHierarchyId)
            if (hierarchyItem && hierarchyItem.Location && hierarchyItem.Location.IANATimezoneId) {
                this.defaultTimezoneId = hierarchyItem.Location.IANATimezoneId
            }
        }
        this.state = {
            address: address,
            timezoneId: (props.geoLocation.Location && props.geoLocation.Location.IANATimezoneId) ?
                props.geoLocation.Location.IANATimezoneId :
                this.defaultTimezoneId,
            allowGPSLocation: props.geoLocation.IsLocation,
            allowMicroLocation: props.geoLocation.IsMicroLocation,
            disableLocationExceptions: props.geoLocation.DisableLocationException,
            addressSearch: "",
            latitude: (address ? address.Latitude : "") as string,
            errorLatitude: address ? "" : strings.Input_a_valid_value,
            longitude: (address ? address.Longitude : "") as string,
            errorLongitude: address ? "" : strings.Input_a_valid_value,
            geoFencePerimeter: props.geoLocation.Location && props.geoLocation.Location.GeoFencePerimeter ?
                props.geoLocation.Location.GeoFencePerimeter :
                this.defaultGeoFencePerimeter,
            errorGeoFencePerimeter: "",
            microLocationValidation: props.geoLocation.MicroLocation && props.geoLocation.MicroLocation.BeaconMajorVersion ? true : false,
            microLocation: props.geoLocation.MicroLocation,
            beaconCodeErrorText: ""
        }
        this.Geocoder = new google.maps.Geocoder()
        autobind(this)
    }

    componentDidMount() {
        AccountStore.on("timezonedata_change", this.timezoneDateChangeEvent)
    }

    componentWillUnmount() {
        AccountStore.removeListener("timezonedata_change", this.timezoneDateChangeEvent)
    }

    componentWillReceiveProps(nextProps: IProps) {
        if (this.props.objectId === nextProps.objectId) return

        const address = nextProps.geoLocation.Location && nextProps.geoLocation.Location.Address
        this.setState({
            address,
            timezoneId: (nextProps.geoLocation.Location && nextProps.geoLocation.Location.IANATimezoneId) ?
                nextProps.geoLocation.Location.IANATimezoneId :
                this.defaultTimezoneId,
            allowGPSLocation: nextProps.geoLocation.IsLocation,
            allowMicroLocation: nextProps.geoLocation.IsMicroLocation,
            disableLocationExceptions: nextProps.geoLocation.DisableLocationException,
            addressSearch: "",
            latitude: (address ? address.Latitude : "") as string,
            errorLatitude: address ? "" : strings.Input_a_valid_value,
            longitude: (address ? address.Longitude : "") as string,
            errorLongitude: address ? "" : strings.Input_a_valid_value,
            geoFencePerimeter: nextProps.geoLocation.Location && nextProps.geoLocation.Location.GeoFencePerimeter ?
                nextProps.geoLocation.Location.GeoFencePerimeter :
                this.defaultGeoFencePerimeter,
            errorGeoFencePerimeter: "",
            microLocationValidation: nextProps.geoLocation.MicroLocation && nextProps.geoLocation.MicroLocation.BeaconMajorVersion ? true : false,
            microLocation: nextProps.geoLocation.MicroLocation,
            beaconCodeErrorText: ""
        })
    }

    timezoneDateChangeEvent() {
        this.forceUpdate()
    }

    changeLatitude(e) {
        const latitude = e.target.value
        this.setState({ latitude }, () =>
            (latitude === "" || !reg.test(latitude) || parseFloat(latitude) > 90 || parseFloat(latitude) < -90) ?
                this.setState({ errorLatitude: strings.Input_a_valid_value }) :
                this.setState({ errorLatitude: "" }, () => this.reverseGeoCode())
        )
    }

    changeLongitude(e) {
        const longitude = e.target.value
        this.setState({ longitude }, () =>
            (longitude === "" || !reg.test(longitude) || parseFloat(longitude) > 180 || parseFloat(longitude) < -180) ?
                this.setState({ errorLongitude: strings.Input_a_valid_value }) :
                this.setState({ errorLongitude: "" }, () => this.reverseGeoCode())
        )
    }

    setMarkerByMapClick(e) {
        const {
            latLng: { lat, lng }
        } = e
        const address = {
            Latitude: lat(),
            Longitude: lng()
        }
        this.setState({
            latitude: lat(),
            longitude: lng(),
            address,
            errorLatitude: "",
            errorLongitude: ""
        }, () =>
            this.handleChange()
        )
    }

    reverseGeoCode() {
        if (this.state.errorLatitude === strings.Input_a_valid_value || this.state.errorLongitude === strings.Input_a_valid_value) return
        const lat_lng = {
            lat: parseFloat(this.state.latitude),
            lng: parseFloat(this.state.longitude)
        }
        this.Geocoder.geocode({ location: lat_lng }, (result, status) => {
            status === "OK" ?
                this.setState({
                    address: {
                        Longitude: parseFloat(this.state.longitude),
                        Latitude: parseFloat(this.state.latitude),
                        StreetName: result[0].formatted_address
                    },
                    errorLatitude: "",
                    errorLongitude: ""
                }) :
                this.setState({
                    errorLatitude: strings.Cannot_find_a_valid_address,
                    errorLongitude: strings.Cannot_find_a_valid_address
                })
        })
    }

    handleAddress() {
        if (this.state.errorLatitude || this.state.errorLongitude || this.state.errorGeoFencePerimeter) return
        this.handleChange()
    }

    changeGeoFencePerimeter(e) {
        const value = parseFloat(e.target.value) || 0
        this.setState({
            geoFencePerimeter: value,
            errorGeoFencePerimeter: (value < 0 || value > 500000) ? strings.formatString(strings.This_field_requires_a_number_between_min_and_max, 0, 500000) as string : ""
        })
    }

    changeBeaconRegCode(e) {
        const code = e.target.value.toUpperCase().trim()
        this.setState({ microLocation: { ActivationCode: code } })
        if (!code.length) return
        api.getMicroLocationByActivationCode(AccountStore.getCurrentAccount().Id, code).then(res => {
            res.data[0] === null ?
                this.setState({
                    beaconCodeErrorText: strings.CannotFindActivationCode,
                    microLocationValidation: false
                }) :
                this.setState({
                    beaconCodeErrorText: "",
                    microLocation: res.data[0],
                    microLocationValidation: true
                }, () =>
                    this.handleChange()
                )
        })
    }

    onAddressSelect(addressSearch) {
        geocodeByAddress(addressSearch, (error, { lat, lng }, results) => {
            if (!error && results && results.length > 0) {
                const place = results[0]
                const addressComponents = place.address_components
                const route = addressComponents.find(component => component.types.includes('route'))
                const locality = addressComponents.find(component => component.types.includes('locality'))
                const administrative_area_level_1 = addressComponents.find(component => component.types.includes('administrative_area_level_1'))
                const country = addressComponents.find(component => component.types.includes('country'))
                const postalCode = addressComponents.find(component => component.types.includes('postal_code'))
                const streetNumber = addressComponents.find(component => component.types.includes('street_number'))
                const address: IAddress = {
                    Latitude: place.geometry.location.lat(),
                    Longitude: place.geometry.location.lng(),
                    StreetNumber: streetNumber ? streetNumber.long_name : null,
                    StreetName: route ? route.long_name : null,
                    CityTown: locality ? locality.long_name : null,
                    GoverningDistrictOrState: administrative_area_level_1 ? administrative_area_level_1.short_name : null,
                    Country: country ? country.long_name : null,
                    PostalArea: postalCode ? postalCode.long_name : null
                }
                this.setState({
                    address,
                    addressSearch,
                    latitude: lat,
                    longitude: lng,
                    errorLatitude: "",
                    errorLongitude: "",
                    geoFencePerimeter: this.defaultGeoFencePerimeter,
                    errorGeoFencePerimeter: ""
                }, () =>
                    this.handleAddress()
                )
            }
        })
    }

    handleChange() {
        const geoLocation = _.cloneDeep(this.props.geoLocation)
        geoLocation.IsLocation = this.state.allowGPSLocation
        geoLocation.IsMicroLocation = this.state.allowMicroLocation
        geoLocation.DisableLocationException = this.state.disableLocationExceptions

        if (this.state.allowGPSLocation) {
            if (this.state.errorLatitude || this.state.errorLongitude || this.state.errorGeoFencePerimeter) return
            if (!geoLocation.Location) {
                geoLocation.Location = { Address: {} }
            }
            geoLocation.Location.GeoFencePerimeter = this.state.geoFencePerimeter
            geoLocation.Location.Address = this.state.address
            geoLocation.MicroLocation = null
        }
        else {
            geoLocation.Location = null
        }

        if (this.state.allowMicroLocation) {
            if (this.state.beaconCodeErrorText) return
            geoLocation.MicroLocation = this.state.microLocation
            geoLocation.Location = null
        }
        else {
            geoLocation.MicroLocation = null
        }

        if (this.state.timezoneId !== this.defaultTimezoneId) {
            if (geoLocation.Location) {
                geoLocation.Location.IANATimezoneId = this.state.timezoneId
            }
            else {
                geoLocation.Location = { IANATimezoneId: this.state.timezoneId }
            }
        }
        else {
            if (geoLocation.Location) {
                geoLocation.Location.IANATimezoneId = this.state.timezoneId
            }
            else {
                geoLocation.Location = null
            }
        }
        this.props.handleChangeGeoLocation(geoLocation)
    }

    render() {
        return (
            <div>
                {(!this.props.jobLocation || AccountStore.getJobTimezoneEnabled()) &&
                    <SelectField
                        data-intercom-target={"menu-Select-TimeZone"}
                        style={{ width: 300, marginBottom: 20 }}
                        floatingLabelText={this.props.jobLocation ? strings.Job_Timezone : strings.Hierarchy_Timezone}
                        value={this.state.timezoneId}
                        onChange={(event, index, value) => this.setState({ timezoneId: value }, () => this.handleChange())}
                        disabled={this.props.readonly}
                    >
                        {AccountStore.getTimezones().map((timezone, index) => (
                            <MenuItem value={timezone.Name} primaryText={timezone.Label} key={index} />
                        ))}
                    </SelectField>
                }
                <Toggle
                    data-intercom-target={"menu-LocationTab-Toggle-ThisIsLocation"}
                    disabled={this.props.readonly || this.props.disabledGPSLocation}
                    onToggle={(e, checked) =>
                        this.setState({
                            allowGPSLocation: checked,
                            allowMicroLocation: checked ? false : this.state.allowMicroLocation,
                            microLocation: checked ? null : this.state.microLocation
                        }, () =>
                            this.handleChange()
                        )
                    }
                    style={{ paddingBottom: 12, width: 300 }}
                    defaultToggled={this.state.allowGPSLocation}
                    label={strings.ThisIsALocation}
                />
                <Toggle
                    data-intercom-target={"menu-LocationTab-Toggle-ThisIsMicroLocation"}
                    disabled={this.props.readonly || this.props.disabledMicroLocation}
                    onToggle={(e, checked) =>
                        this.setState({
                            allowMicroLocation: checked,
                            allowGPSLocation: checked ? false : this.state.allowGPSLocation,
                            address: checked ? null : this.state.address,
                            latitude: checked ? "" : this.state.latitude,
                            longitude: checked ? "" : this.state.longitude
                        }, () =>
                            this.handleChange()
                        )
                    }
                    style={{ paddingBottom: 12, width: 300 }}
                    defaultToggled={this.state.allowMicroLocation}
                    label={strings.ThisIsAMicroLocation}
                />
                <Toggle
                    data-intercom-target={"menu-LocationTab-Toggle-EnableLocationException"}
                    disabled={this.props.readonly}
                    style={{ paddingBottom: 12, width: 300 }}
                    label={strings.EnableLocationException}
                    defaultToggled={!this.state.disableLocationExceptions}
                    onToggle={() => this.setState({ disableLocationExceptions: !this.state.disableLocationExceptions },
                        () => this.handleChange()
                    )}
                />
                <Paper zDepth={1}>
                    {this.state.allowGPSLocation &&
                        <div style={{ margin: 0 }}>
                            <div style={styles.row as React.CSSProperties}>
                                {!this.props.readonly &&
                                    <div
                                        data-intercom-target={"menu-LocationTab-Entry-SearchLocation"}
                                        title={this.state.addressSearch}
                                        style={{ marginTop: 20, width: "100%" }}
                                    >
                                        <Autocomplete
                                            styles={styles.autocomplete}
                                            highlightFirstSuggestion
                                            inputProps={{
                                                value: this.state.addressSearch,
                                                onChange: addressSearch => this.setState({ addressSearch }),
                                                placeholder: strings.Search_Location
                                            }}
                                            onSelect={this.onAddressSelect}
                                        />
                                    </div>
                                }
                            </div>
                            <div style={styles.row as React.CSSProperties}>
                                <TextField
                                    data-intercom-target={"menu-LocationTab-Entry-Latitude"}
                                    style={styles.textField}
                                    floatingLabelText={strings.Latitude}
                                    value={this.state.latitude}
                                    onChange={this.changeLatitude}
                                    onBlur={this.handleAddress}
                                    disabled={this.props.readonly}
                                    errorText={this.state.errorLatitude}
                                    errorStyle={styles.errorStyle}
                                />
                                <TextField
                                    data-intercom-target={"menu-LocationTab-Entry-Longitude"}
                                    style={styles.textField}
                                    floatingLabelText={strings.Longitude}
                                    value={this.state.longitude}
                                    onChange={this.changeLongitude}
                                    onBlur={this.handleAddress}
                                    disabled={this.props.readonly}
                                    errorText={this.state.errorLongitude}
                                    errorStyle={styles.errorStyle}
                                />
                                <TextField
                                    data-intercom-target={"menu-LocationTab-Entry-GeofencePerimeter"}
                                    style={styles.textField}
                                    floatingLabelText={strings.Geofence_Perimeter}
                                    value={this.state.geoFencePerimeter}
                                    onChange={this.changeGeoFencePerimeter}
                                    onBlur={this.handleChange}
                                    disabled={this.props.readonly}
                                    errorText={this.state.errorGeoFencePerimeter}
                                    errorStyle={styles.errorStyle}
                                />
                            </div>
                            <div style={styles.map as React.CSSProperties}>
                                <LocationMap
                                    address={this.state.address}
                                    geoFencePerimeter={this.state.geoFencePerimeter}
                                    onMapClick={e => {
                                        if (this.props.readonly) return
                                        AppStateStore.setConfirmDialogContent({
                                            content: strings.Are_you_sure_you_want_to_change_location,
                                            confirm: () => this.setMarkerByMapClick(e)
                                        })
                                    }}
                                />
                            </div>
                        </div>
                    }
                    {this.state.allowMicroLocation &&
                        <div>
                            <div>
                                <TextField
                                    data-intercom-target={"menu-LocationTab-Entry-BeaconRegistrationCode"}
                                    style={{ display: "inline-block", margin: "20px 30px" }}
                                    floatingLabelText={strings.Beacon_Registration_Code}
                                    value={this.state.microLocation ? this.state.microLocation.ActivationCode : ""}
                                    onChange={this.changeBeaconRegCode}
                                    errorText={this.state.beaconCodeErrorText}
                                    disabled={this.props.readonly}
                                />
                                {this.state.microLocationValidation &&
                                    <i
                                        className="fa fa-check-circle-o"
                                        aria-hidden="true"
                                        style={{ marginRight: 10, fontSize: 25, color: "#27ae60", display: "inline-block" }}
                                    />
                                }
                            </div>
                            {this.state.microLocationValidation &&
                                <div style={{ margin: "0 30px", paddingBottom: 15 }}>
                                    <p>UUID: {this.state.microLocation && this.state.microLocation.BeaconUUID}</p>
                                    <p>Major: {this.state.microLocation && this.state.microLocation.BeaconMajorVersion}</p>
                                    <p>Minor: {this.state.microLocation && this.state.microLocation.BeaconMinorVersion}</p>
                                </div>
                            }
                        </div>
                    }
                </Paper>
            </div>
        )
    }
}

const styles = {
    row: {
        display: "flex",
        flexWrap: "nowrap",
        justifyContent: "space-around"
    },
    autocomplete: {
        flex: 1,
        root: {
            width: "90%",
            display: "inline-block",
            margin: "20px 30px 0px 30px"
        },
        input: {
            border: 0,
            borderBottom: "1px solid rgba(20,20,20,.1)",
            paddingBottom: 5,
        },
        autocompleteContainer: { paddingLeft: "5px", zIndex: 100 }
    },
    textField: {
        flex: 4,
        margin: "20px 30px 20px 30px"
    },
    errorStyle: {
        position: "absolute",
        bottom: -10
    },
    map: {
        marginTop: 10,
        height: 750,
        position: "relative"
    }
}